import React from "react";
import { useTranslation } from "react-i18next";
import { changeOf, formatNumber } from "../../../../utils/numbers";
import { TrendChip } from "./ActiveUsersTrend";

const display = (value) => (value === null ? "—" : formatNumber(value));

/** Five stars filled up to `rating` (out of 5). */
export const RatingStars = ({ rating }) => (
  <span
    className="dash-stars"
    role="img"
    aria-label={`${rating.toFixed(1)} / 5`}
  >
    <span aria-hidden="true">★★★★★</span>
    <span
      className="dash-stars-fill"
      style={{ width: `${(Math.min(rating, 5) / 5) * 100}%` }}
      aria-hidden="true"
    >
      ★★★★★
    </span>
  </span>
);

/** Downloads, audience and rating of the app on one store. */
const StoreCard = ({ name, icon, color, url, stats, loading }) => {
  const { t } = useTranslation();

  return (
    <section className="dash-card dash-store" style={{ "--store-color": color }}>
      <div className="dash-card-header">
        <div className="dash-store-name">
          <span className="dash-store-icon">
            <i className={icon} aria-hidden="true"></i>
          </span>
          <h2 className="dash-card-title">{name}</h2>
        </div>
        <a
          className="dash-store-link"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("View on the store")}
          <i className="las la-external-link-alt" aria-hidden="true"></i>
        </a>
      </div>

      {loading ? (
        <div className="dash-skeleton" style={{ height: 170 }} />
      ) : !stats ? (
        <p className="dash-empty">{t("Store statistics are unavailable")}</p>
      ) : (
        <>
          <div className="dash-store-total">
            <span className="dash-store-total-value">
              {display(stats.downloads)}
            </span>
            <span className="dash-store-total-label">
              {t("total downloads")}
              {stats.tier && <span className="dash-store-tier">{stats.tier}</span>}
            </span>
          </div>

          <dl className="dash-store-stats">
            <div>
              <dt>{t("Last 30 days")}</dt>
              <dd>
                {stats.downloadsLast30Days === null
                  ? "—"
                  : `+${formatNumber(stats.downloadsLast30Days)}`}
              </dd>
            </div>
            <div>
              <dt>{t("Active devices")}</dt>
              <dd>
                {display(stats.activeDevices)}
                <TrendChip
                  change={changeOf(stats.activeDevices, stats.activeDevicesPrevious)}
                  title={t("vs {{previous}} the previous 30 days", {
                    previous: formatNumber(stats.activeDevicesPrevious),
                  })}
                />
              </dd>
            </div>
            <div>
              <dt>{t("Reviews")}</dt>
              <dd>{display(stats.reviews)}</dd>
            </div>
          </dl>

          <div className="dash-store-rating">
            {stats.rating ? (
              <>
                <RatingStars rating={stats.rating} />
                <strong>{stats.rating.toFixed(1)}</strong>
                <span>/ 5</span>
              </>
            ) : (
              <span>{t("Not rated yet")}</span>
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default StoreCard;
